// UTM capture with first-touch persistence.
// - Reads utm_* params from current URL and persists them to localStorage.
// - First-touch: never overwrites existing values (attribution stays with first visit).
// - Also stores utm_referrer (document.referrer) and utm_landing_page (first URL seen).
// - TTL: 30 days.

const STORAGE_KEY = "bz_utm_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type UtmData = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  utm_referrer: string | null;
  utm_landing_page: string | null;
};

const EMPTY: UtmData = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  utm_referrer: null,
  utm_landing_page: null,
};

type Stored = { savedAt: number; data: UtmData };

function safeRead(): Stored | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function safeWrite(data: UtmData) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), data } satisfies Stored),
    );
  } catch {
    /* quota / private mode: ignore */
  }
}

function truncate(v: string | null, max: number): string | null {
  if (!v) return null;
  const t = v.trim();
  if (!t) return null;
  return t.slice(0, max);
}

/**
 * Captures UTM params from the current URL and persists them (first-touch).
 * Safe to call on every route change; existing values are preserved.
 */
export function captureUtmsFromUrl() {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const sp = url.searchParams;

  const incoming: Partial<UtmData> = {
    utm_source: truncate(sp.get("utm_source"), 255),
    utm_medium: truncate(sp.get("utm_medium"), 255),
    utm_campaign: truncate(sp.get("utm_campaign"), 255),
    utm_term: truncate(sp.get("utm_term"), 255),
    utm_content: truncate(sp.get("utm_content"), 255),
  };

  const hasAny = Object.values(incoming).some((v) => v);
  const stored = safeRead();

  // If no UTMs stored yet, seed landing_page + referrer even without utm_* params
  // (so we still attribute organic/direct traffic).
  if (!stored) {
    const seed: UtmData = {
      ...EMPTY,
      ...incoming,
      utm_referrer: truncate(document.referrer || null, 2000),
      utm_landing_page: truncate(window.location.href, 2000),
    };
    safeWrite(seed);
    return;
  }

  // First-touch: only fill fields that are still empty.
  if (hasAny) {
    const merged: UtmData = { ...stored.data };
    (Object.keys(incoming) as (keyof UtmData)[]).forEach((k) => {
      if (!merged[k] && incoming[k]) merged[k] = incoming[k] ?? null;
    });
    safeWrite(merged);
  }
}

export function getStoredUtms(): UtmData {
  const s = safeRead();
  return s?.data ?? { ...EMPTY };
}

/** Returns UTMs shaped for inclusion in signup metadata / server calls. */
export function getUtmsForSignup(): UtmData {
  return getStoredUtms();
}

/** Clear after a successful attribution write (optional). */
export function clearStoredUtms() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
